import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../../../interceptors";

const STATUS_OPTIONS = ["", "SENT", "READY", "QUEUED", "BOUNCED", "FAILED"];
const ACTION_OPTIONS = ["", "CONFIRMED", "DECLINED", "RESCHEDULED"];
const DIRECTION_OPTIONS = ["ALL", "OUTBOUND", "INBOUND"];

const statusBadge = (s) => {
  if (s === "SENT") return { bg: "#EEF2FF", color: "#4338CA" };
  // READY = dispatcher-eligible: recipient resolved, awaiting next tick.
  // Visually distinct from QUEUED (no recipient) so admins can tell them apart.
  if (s === "READY") return { bg: "#DBEAFE", color: "#1E40AF" };
  if (s === "QUEUED") return { bg: "#FEF3C7", color: "#92400E" };
  if (s === "BOUNCED") return { bg: "#FEE2E2", color: "#991B1B" };
  if (s === "FAILED") return { bg: "#FEE2E2", color: "#991B1B" };
  return { bg: "#F1F5F9", color: "#475569" };
};

const EmailInbox = () => {
  const navigate = useNavigate();
  // URL query params let the dashboard email-activity widget deep-link
  // straight to a filtered inbox view ("Bounced 12" → ?status=BOUNCED).
  const [searchParams] = useSearchParams();
  const initialStatus = (searchParams.get("status") || "").toUpperCase();
  const initialDirection = (searchParams.get("direction") || "ALL").toUpperCase();
  const initialClassification = (searchParams.get("classification") || "").toUpperCase();
  const initialTemplateKey = searchParams.get("templateKey") || "";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    direction: ["ALL", "OUTBOUND", "INBOUND"].includes(initialDirection) ? initialDirection : "ALL",
    status: STATUS_OPTIONS.includes(initialStatus) ? initialStatus : "",
    classification: ACTION_OPTIONS.includes(initialClassification) ? initialClassification : "",
    templateKey: initialTemplateKey,
    fromDate: "",
    toDate: "",
    search: "",
  });
  // Dropdown source — pulled from the backend so it shows only keys actually
  // present in this tenant's data instead of a hardcoded list.
  const [templateKeys, setTemplateKeys] = useState([]);

  const load = useCallback(async (p = page, f = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (f.direction && f.direction !== "ALL") params.set("direction", f.direction);
      if (f.status) params.set("status", f.status);
      if (f.classification) params.set("classification", f.classification);
      if (f.templateKey) params.set("templateKey", f.templateKey);
      if (f.fromDate) params.set("fromDate", f.fromDate);
      if (f.toDate) params.set("toDate", f.toDate);
      if (f.search) params.set("search", f.search);
      params.set("page", p);
      params.set("size", size);
      const { data } = await axiosInstance.get(`/common/email-logs?${params.toString()}`);
      if (data?.success) {
        setRows(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      setRows([]); setTotal(0);
    } finally { setLoading(false); }
  }, [page, size, filters]);

  useEffect(() => {
    document.title = "Email inbox - Ample Hire";
    load(0, filters);
    // Fetch distinct template_keys for the filter dropdown — only the keys
    // actually present in this tenant.
    axiosInstance.get("/common/email-logs/template-keys")
      .then((r) => { if (r.data?.success) setTemplateKeys(r.data.templateKeys || []); })
      .catch(() => setTemplateKeys([]));
    /* eslint-disable-next-line */
  }, []);

  const apply = () => { setPage(0); load(0, filters); };
  const clear = () => {
    const empty = { direction: "ALL", status: "", classification: "", templateKey: "", fromDate: "", toDate: "", search: "" };
    setFilters(empty); setPage(0); load(0, empty);
  };
  const onPage = (p) => { setPage(p); load(p, filters); };

  const totalPages = Math.max(1, Math.ceil(total / size));

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Email inbox</h1>
          <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0" }}>
            All outbound and inbound emails for your organization. {total} total.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => navigate("/admin/compose-email")} style={btnPrimary}>+ Compose</button>
          <button onClick={() => navigate("/admin/email-settings")} style={btnGhost}>Settings</button>
        </div>
      </div>

      <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 12, padding: 14, marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
          <Field label="Direction">
            <select value={filters.direction} onChange={(e) => setFilters({ ...filters, direction: e.target.value })} style={input}>
              {DIRECTION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} style={input}>
              {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o || "Any"}</option>)}
            </select>
          </Field>
          <Field label="Template">
            <select value={filters.templateKey} onChange={(e) => setFilters({ ...filters, templateKey: e.target.value })} style={input}>
              <option value="">Any</option>
              {templateKeys.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </Field>
          <Field label="Classification">
            <select value={filters.classification} onChange={(e) => setFilters({ ...filters, classification: e.target.value })} style={input}>
              {ACTION_OPTIONS.map((o) => <option key={o} value={o}>{o || "Any"}</option>)}
            </select>
          </Field>
          <Field label="From"><input type="date" value={filters.fromDate} onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })} style={input} /></Field>
          <Field label="To"><input type="date" value={filters.toDate} onChange={(e) => setFilters({ ...filters, toDate: e.target.value })} style={input} /></Field>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <Field label="Search" style={{ flex: 1, marginBottom: 0 }}>
            <input type="text" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="Recipient, subject, name..." style={input} onKeyDown={(e) => e.key === "Enter" && apply()} />
          </Field>
          <button onClick={apply} style={btnPrimary}>Apply</button>
          <button onClick={clear} style={btnGhost}>Clear</button>
        </div>
      </div>

      <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "#F8FAFC" }}>
            <tr>
              {["Recipient", "Subject", "Template", "Status", "Classification", "Sent"].map((h) => (
                <th key={h} style={{ textAlign: "left", padding: "10px 14px", fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600, borderBottom: "1px solid #E2E8F0" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>No emails match these filters.</td></tr>
            )}
            {!loading && rows.map((r) => {
              const sb = statusBadge(r.status);
              return (
                <tr key={r.id} onClick={() => navigate(`/admin/email-detail/${r.id}`)} style={{ cursor: "pointer", borderBottom: "1px solid #F1F5F9" }}>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ fontWeight: 500, color: "#0F172A" }}>{r.recipientName || r.recipientEmail}</div>
                    <div style={{ fontSize: 11, color: "#94A3B8" }}>{r.recipientEmail}</div>
                  </td>
                  <td style={{ padding: "10px 14px", color: "#334155", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.subject || "—"}</td>
                  <td style={{ padding: "10px 14px", color: "#64748B", fontSize: 11 }}>{r.templateKey || "—"}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 100, background: sb.bg, color: sb.color, fontWeight: 600 }}>{r.status}</span>
                  </td>
                  <td style={{ padding: "10px 14px", fontSize: 11 }}>
                    {r.aiProcessed ? (
                      <span style={{ padding: "2px 8px", borderRadius: 100, background: "#F5F3FF", color: "#7C3AED", fontWeight: 600 }}>
                        AI · {r.aiAction || "processed"}
                      </span>
                    ) : <span style={{ color: "#94A3B8" }}>—</span>}
                  </td>
                  <td style={{ padding: "10px 14px", fontSize: 11, color: "#64748B" }}>
                    {r.sentAt ? new Date(r.sentAt).toLocaleString() : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, fontSize: 12, color: "#64748B" }}>
          <span>Page {page + 1} of {totalPages} · {total} total</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => onPage(Math.max(0, page - 1))} disabled={page === 0} style={{ ...btnGhost, opacity: page === 0 ? 0.4 : 1 }}>Previous</button>
            <button onClick={() => onPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} style={{ ...btnGhost, opacity: page >= totalPages - 1 ? 0.4 : 1 }}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children, style }) => (
  <div style={{ marginBottom: 10, ...style }}>
    <div style={{ fontSize: 11, color: "#64748B", marginBottom: 4, fontWeight: 500 }}>{label}</div>
    {children}
  </div>
);

const input = { width: "100%", padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 12, color: "#0F172A", outline: "none", background: "white", boxSizing: "border-box" };
const btnPrimary = { padding: "8px 16px", borderRadius: 6, background: "#7B5AC4", color: "white", border: "none", fontWeight: 600, fontSize: 12, cursor: "pointer" };
const btnGhost = { padding: "8px 16px", borderRadius: 6, background: "white", color: "#475569", border: "1px solid #E2E8F0", fontWeight: 600, fontSize: 12, cursor: "pointer" };

export default EmailInbox;
